import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import {
  Copy, Check, ChevronRight, Lock, BookOpen, RefreshCw, 
  CalendarDays, BedDouble, BarChart3, Bell, AlertTriangle,
  Code, ArrowRight, Mail, ExternalLink, Search,
  Globe, Users, FlaskConical, ShieldCheck, History
} from 'lucide-react';

/* ─── Code Block with Copy ─── */
function CodeBlock({ title, lang = 'json', children }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl overflow-hidden border border-slate-700 bg-[#0F172A] my-4" data-testid={`code-block-${title?.toLowerCase().replace(/\s/g, '-') || 'default'}`}>
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800/80 border-b border-slate-700">
          <span className="text-xs font-mono text-slate-400">{title}</span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{lang}</span>
            <button onClick={handleCopy} className="p-1 hover:bg-slate-700 rounded transition-colors" title="Copy code">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
            </button>
          </div>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed font-mono">
        <code className="text-slate-300">{children}</code>
      </pre>
    </div>
  );
}

/* ─── Method Badge ─── */
function MethodBadge({ method }) {
  const styles = {
    GET: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    POST: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    PUT: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    DELETE: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-bold border font-mono ${styles[method] || ''}`}>
      {method}
    </span>
  );
}

/* ─── Endpoint Header ─── */
function Endpoint({ method, path, description }) {
  return (
    <div className="mb-6 pb-2 border-b border-slate-200" data-testid={`endpoint-${method.toLowerCase()}-${path.replace(/[/{}/]/g, '-').replace(/^-|-$/g, '')}`}>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <MethodBadge method={method} />
        <code className="text-sm font-mono font-semibold text-slate-800 bg-slate-100 px-3 py-1 rounded-lg">{path}</code>
      </div>
      <p className="text-slate-600 text-sm">{description}</p>
    </div>
  );
}

/* ─── Parameter Table ─── */
function ParamTable({ params }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm" data-testid="param-table">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 pr-4 text-slate-500 font-medium text-xs uppercase tracking-wider">Parameter</th>
            <th className="text-left py-2 pr-4 text-slate-500 font-medium text-xs uppercase tracking-wider">Type</th>
            <th className="text-left py-2 pr-4 text-slate-500 font-medium text-xs uppercase tracking-wider">Required</th>
            <th className="text-left py-2 text-slate-500 font-medium text-xs uppercase tracking-wider">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2.5 pr-4"><code className="text-xs font-mono text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{p.name}</code></td>
              <td className="py-2.5 pr-4 text-slate-500 text-xs">{p.type}</td>
              <td className="py-2.5 pr-4">
                {p.required ? (
                  <span className="text-xs font-medium text-red-500">Required</span>
                ) : (
                  <span className="text-xs text-slate-400">Optional</span>
                )}
              </td>
              <td className="py-2.5 text-slate-600 text-xs">{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Section Component ─── */
function DocSection({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 mb-16" data-testid={`section-${id}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#2563EB]" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
      </div>
      <div className="text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════
   API DOCUMENTATION PAGE
   ═══════════════════════════════════════════════════ */
const ApiDocs = () => {
  const [activeSection, setActiveSection] = useState('authentication');
  const [searchQuery, setSearchQuery] = useState('');
  const mainRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Track active section on scroll
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0.1 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'base-url', label: 'Base URL', icon: Globe },
    { id: 'authentication', label: 'Authentication', icon: Lock },
    { id: 'idempotency', label: 'Idempotency', icon: ShieldCheck },
    { id: 'booking-sync', label: 'Booking Sync', icon: RefreshCw },
    { id: 'reservation-updates', label: 'Reservation Updates', icon: CalendarDays },
    { id: 'properties', label: 'Properties', icon: BedDouble },
    { id: 'guests', label: 'Guests', icon: Users },
    { id: 'availability-inventory', label: 'Availability & Inventory', icon: BedDouble },
    { id: 'rates', label: 'Rates', icon: BarChart3 },
    { id: 'webhooks', label: 'Webhooks', icon: Bell },
    { id: 'error-codes', label: 'Error Codes', icon: AlertTriangle },
    { id: 'sandbox', label: 'Sandbox / Test Mode', icon: FlaskConical },
    { id: 'changelog', label: 'API Changelog', icon: History },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const filteredItems = searchQuery
    ? sidebarItems.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : sidebarItems;

  return (
    <>
      <SEO
        title="StayMii API Documentation | PMS Integration & e-Invoice API"
        description="Complete API documentation for StayMii Lite integration. Endpoints for bookings, rooms, availability, rates, and webhooks. Build your channel manager or PMS integration."
        keywords="StayMii API docs, PMS API, channel manager API, hotel booking API, availability API, rate management API, webhook integration, Malaysia e-invoice API"
        canonicalUrl="/docs"
      />

      <div className="min-h-screen bg-white" data-testid="api-docs-page">
        <LandingNavBar />

        {/* Hero */}
        <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-16 overflow-hidden" data-testid="api-docs-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/8 rounded-full blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/15 rounded-full text-blue-300 text-xs font-medium">
                <BookOpen className="h-3.5 w-3.5" /> API Reference v1.0
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/15 rounded-full text-emerald-300 text-xs font-medium">
                REST API
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              StayMii API Documentation
            </h1>
            <p className="text-base lg:text-lg text-white/70 max-w-3xl leading-relaxed mb-6">
              Integrate your PMS, channel manager, or booking platform with StayMii. 
              Full RESTful API for bookings, availability, rates, and LHDN e-invoice automation.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="text-xs text-white/50 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg font-mono">
                Base URL: https://api.staymii.com/v1
              </span>
              <span className="text-xs text-white/50 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg font-mono">
                Format: JSON
              </span>
              <span className="text-xs text-white/50 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg font-mono">
                Auth: Bearer Token
              </span>
            </div>
          </div>
        </section>

        {/* Main Content: Sidebar + Docs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="flex gap-10">

            {/* Sidebar */}
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-28">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search sections..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                    data-testid="api-docs-search"
                  />
                </div>
                <nav className="space-y-1" data-testid="api-docs-sidebar">
                  {filteredItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        activeSection === item.id
                          ? 'bg-[#2563EB]/10 text-[#2563EB]'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      data-testid={`sidebar-${item.id}`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </button>
                  ))}
                </nav>

                <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Need Help?</h4>
                  <p className="text-xs text-slate-500 mb-3">Contact our integration team for onboarding support.</p>
                  <a href="mailto:developer@staymii.com" className="flex items-center gap-1.5 text-xs font-medium text-[#2563EB] hover:underline">
                    <Mail className="w-3.5 h-3.5" /> developer@staymii.com
                  </a>
                </div>
              </div>
            </aside>

            {/* Content */}
            <main className="flex-1 min-w-0" ref={mainRef}>

              {/* ═══ OVERVIEW ═══ */}
              <DocSection id="overview" icon={BookOpen} title="API Overview">
                <p className="mb-4">
                  StayMii API enables hotels, channel managers, and third-party hospitality platforms to integrate 
                  with <strong>StayMii Lite</strong> and <strong>StayMii e-Invoice</strong> services. The API supports 
                  booking synchronization, availability queries, rate management, and Malaysian LHDN MyInvois e-invoice submission.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 my-6">
                  {[
                    { title: 'Booking Sync', desc: 'Push and pull bookings between your PMS and StayMii in real-time.' },
                    { title: 'Availability & Rates', desc: 'Query room availability and manage pricing across all channels.' },
                    { title: 'e-Invoice (LHDN)', desc: 'Automate Malaysian tax invoice submission via MyInvois.' },
                    { title: 'Webhooks', desc: 'Receive instant notifications on booking and invoice events.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                  <p className="text-sm text-blue-800">
                    <strong>Getting Started:</strong> Request your API credentials from{' '}
                    <a href="mailto:developer@staymii.com" className="font-semibold underline">developer@staymii.com</a>, 
                    then use the sandbox environment to test your integration before going live.
                  </p>
                </div>
              </DocSection>

              {/* ═══ BASE URL ═══ */}
              <DocSection id="base-url" icon={Globe} title="Base URL">
                <p className="mb-4">
                  All API requests are made to the following base URLs. Use the <strong>Sandbox</strong> environment 
                  for development and testing, and switch to <strong>Production</strong> when you are ready to go live.
                </p>

                <div className="space-y-3 my-6">
                  <div className="flex items-stretch rounded-xl overflow-hidden border border-slate-200">
                    <div className="bg-emerald-500 text-white px-4 py-3 flex items-center shrink-0">
                      <span className="text-xs font-bold uppercase tracking-wider">Production</span>
                    </div>
                    <div className="flex-1 bg-slate-50 px-4 py-3 flex items-center">
                      <code className="text-sm font-mono text-slate-800">https://api.staymii.com/v1</code>
                    </div>
                  </div>
                  <div className="flex items-stretch rounded-xl overflow-hidden border border-slate-200">
                    <div className="bg-amber-500 text-white px-4 py-3 flex items-center shrink-0">
                      <span className="text-xs font-bold uppercase tracking-wider">Sandbox</span>
                    </div>
                    <div className="flex-1 bg-slate-50 px-4 py-3 flex items-center">
                      <code className="text-sm font-mono text-slate-800">https://sandbox-api.staymii.com/v1</code>
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Request Format</h3>
                <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside mb-4">
                  <li>All request and response bodies are <strong>JSON</strong></li>
                  <li>Dates follow <strong>ISO 8601</strong> format (<code className="text-xs bg-slate-100 px-1 py-0.5 rounded">YYYY-MM-DD</code>)</li>
                  <li>Timestamps are in <strong>UTC</strong> (<code className="text-xs bg-slate-100 px-1 py-0.5 rounded">YYYY-MM-DDTHH:MM:SSZ</code>)</li>
                  <li>Currency amounts are in <strong>MYR</strong> unless specified</li>
                  <li>Authentication via <strong>Bearer token</strong> in the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Authorization</code> header</li>
                </ul>
              </DocSection>

              {/* ═══ AUTHENTICATION ═══ */}
              <DocSection id="authentication" icon={Lock} title="Authentication">
                <p className="mb-4">
                  All API requests require authentication via a <strong>Bearer token</strong> in the <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">Authorization</code> header. 
                  Tokens are issued upon partner registration and are scoped to your organization.
                </p>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Request Headers</h3>
                <p className="mb-2 text-sm">Every API request must include the following headers:</p>
                <CodeBlock title="Required Headers" lang="http">{`Authorization: Bearer sk_live_your_api_key_here
Content-Type: application/json
X-Partner-ID: your_partner_id`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Obtaining API Credentials</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4">
                  <p className="text-sm text-blue-800">
                    To obtain your API key and Partner ID, contact our integration team at{' '}
                    <a href="mailto:developer@staymii.com" className="font-semibold underline">developer@staymii.com</a>{' '}
                    or request access through your StayMii portal under <strong>Settings &gt; API Keys</strong>.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Authentication Example</h3>
                <CodeBlock title="cURL Example" lang="bash">{`curl -X GET "https://api.staymii.com/v1/bookings" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Authentication Error Response</h3>
                <CodeBlock title="401 Unauthorized" lang="json">{`{
  "error": {
    "code": "UNAUTHORIZED",
    "message": "Invalid or expired API key. Please check your credentials.",
    "status": 401
  }
}`}</CodeBlock>
              </DocSection>

              {/* ═══ IDEMPOTENCY ═══ */}
              <DocSection id="idempotency" icon={ShieldCheck} title="Idempotency Protection">
                <p className="mb-4">
                  Channel managers and PMS integrations may retry failed requests due to network issues. To prevent 
                  duplicate bookings or charges, StayMii supports <strong>idempotency keys</strong> on all write operations 
                  (<code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">POST</code>, <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">PUT</code>, <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">DELETE</code>).
                </p>

                <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3">Usage</h3>
                <p className="text-sm mb-2">Include the <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">Idempotency-Key</code> header with a unique value (UUID recommended) for each distinct operation:</p>
                <CodeBlock title="Idempotent Request" lang="http">{`POST /v1/bookings HTTP/1.1
Authorization: Bearer sk_live_your_api_key_here
Content-Type: application/json
Idempotency-Key: 550e8400-e29b-41d4-a716-446655440000

{
  "property_id": "prop_staymii_hotel",
  "room_id": "room_1505",
  "guest_name": "Ahmad bin Abdullah",
  "check_in": "2026-03-15",
  "check_out": "2026-03-18",
  "total_amount": 750.00
}`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Behaviour</h3>
                <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside mb-4">
                  <li>If the same <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Idempotency-Key</code> is sent again, StayMii returns the original response without re-executing the operation</li>
                  <li>Keys are valid for <strong>24 hours</strong> after the first request</li>
                  <li>Keys are scoped to your partner account &mdash; different partners can use the same key value safely</li>
                  <li>Omitting the header disables idempotency protection for that request</li>
                </ul>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-4">
                  <p className="text-sm text-amber-800">
                    <strong>Recommendation:</strong> Always include an <code className="text-xs bg-amber-200 px-1 py-0.5 rounded font-mono">Idempotency-Key</code> for 
                    booking creation (<code className="text-xs bg-amber-200 px-1 py-0.5 rounded font-mono">POST /v1/bookings</code>) and 
                    rate updates (<code className="text-xs bg-amber-200 px-1 py-0.5 rounded font-mono">POST /v1/rates</code>) to prevent duplicates during retries.
                  </p>
                </div>
              </DocSection>

              {/* ═══ BOOKING SYNC ═══ */}
              <DocSection id="booking-sync" icon={RefreshCw} title="Booking Sync">
                <p className="mb-6">
                  Synchronize booking data between your PMS/channel manager and StayMii. 
                  Create new bookings, retrieve existing ones, or perform bulk syncs for initial onboarding.
                </p>

                {/* POST /api/bookings */}
                <Endpoint 
                  method="POST" 
                  path="/v1/bookings" 
                  description="Create a new booking or push a booking from your PMS to StayMii for e-invoice generation."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Request Body Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: true, desc: 'StayMii property identifier' },
                  { name: 'room_id', type: 'string', required: true, desc: 'Room or unit identifier from your PMS' },
                  { name: 'guest_name', type: 'string', required: true, desc: 'Full name of the primary guest' },
                  { name: 'guest_email', type: 'string', required: false, desc: 'Guest email address' },
                  { name: 'guest_phone', type: 'string', required: false, desc: 'Guest phone number with country code' },
                  { name: 'guest_id_type', type: 'string', required: false, desc: 'ID type: NRIC, PASSPORT, BRN, ARMY' },
                  { name: 'guest_id_number', type: 'string', required: false, desc: 'Guest identification number' },
                  { name: 'check_in', type: 'string (ISO 8601)', required: true, desc: 'Check-in date (e.g., 2026-03-01)' },
                  { name: 'check_out', type: 'string (ISO 8601)', required: true, desc: 'Check-out date (e.g., 2026-03-05)' },
                  { name: 'total_amount', type: 'number', required: true, desc: 'Total booking amount in MYR' },
                  { name: 'currency', type: 'string', required: false, desc: 'Currency code (default: MYR)' },
                  { name: 'status', type: 'string', required: false, desc: 'Booking status: confirmed, cancelled, no_show' },
                  { name: 'source', type: 'string', required: false, desc: 'Booking source: direct, booking_com, agoda, airbnb, expedia' },
                  { name: 'external_booking_id', type: 'string', required: false, desc: 'Your PMS booking reference number' },
                  { name: 'notes', type: 'string', required: false, desc: 'Additional booking notes' },
                ]} />

                <CodeBlock title="Request — POST /v1/bookings" lang="json">{`{
  "property_id": "prop_staymii_hotel",
  "room_id": "room_1505",
  "guest_name": "Ahmad bin Abdullah",
  "guest_email": "ahmad@example.com",
  "guest_phone": "+60123456789",
  "guest_id_type": "NRIC",
  "guest_id_number": "901215-14-5678",
  "check_in": "2026-03-15",
  "check_out": "2026-03-18",
  "total_amount": 750.00,
  "currency": "MYR",
  "status": "confirmed",
  "source": "booking_com",
  "external_booking_id": "BDC-2026031500123",
  "notes": "Late check-in requested"
}`}</CodeBlock>

                <CodeBlock title="Response — 201 Created" lang="json">{`{
  "success": true,
  "data": {
    "booking_id": "bk_abc123def456",
    "property_id": "prop_staymii_hotel",
    "room_id": "room_1505",
    "room_name": "Unit 1505 - 2BR 2BA",
    "guest_name": "Ahmad bin Abdullah",
    "check_in": "2026-03-15",
    "check_out": "2026-03-18",
    "nights": 3,
    "total_amount": 750.00,
    "currency": "MYR",
    "status": "confirmed",
    "source": "booking_com",
    "external_booking_id": "BDC-2026031500123",
    "invoice_status": "pending",
    "created_at": "2026-03-10T08:30:00Z"
  }
}`}</CodeBlock>

                {/* GET /api/bookings */}
                <Endpoint 
                  method="GET" 
                  path="/v1/bookings" 
                  description="Retrieve a list of bookings with optional filters. Supports pagination for large datasets."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Query Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: false, desc: 'Filter by property' },
                  { name: 'check_in_from', type: 'string (ISO 8601)', required: false, desc: 'Filter check-in from date' },
                  { name: 'check_in_to', type: 'string (ISO 8601)', required: false, desc: 'Filter check-in to date' },
                  { name: 'status', type: 'string', required: false, desc: 'Filter by status: confirmed, cancelled, checked_in, checked_out' },
                  { name: 'source', type: 'string', required: false, desc: 'Filter by booking source' },
                  { name: 'page', type: 'integer', required: false, desc: 'Page number (default: 1)' },
                  { name: 'per_page', type: 'integer', required: false, desc: 'Results per page (default: 50, max: 200)' },
                ]} />

                <CodeBlock title="Request — GET /v1/bookings" lang="bash">{`curl -X GET "https://api.staymii.com/v1/bookings?property_id=prop_staymii_hotel&check_in_from=2026-03-01&check_in_to=2026-03-31&status=confirmed&page=1&per_page=50" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": [
    {
      "booking_id": "bk_abc123def456",
      "property_id": "prop_staymii_hotel",
      "room_id": "room_1505",
      "room_name": "Unit 1505 - 2BR 2BA",
      "guest_name": "Ahmad bin Abdullah",
      "check_in": "2026-03-15",
      "check_out": "2026-03-18",
      "nights": 3,
      "total_amount": 750.00,
      "currency": "MYR",
      "status": "confirmed",
      "source": "booking_com",
      "invoice_status": "submitted",
      "lhdn_uuid": "INV-2026-00456",
      "created_at": "2026-03-10T08:30:00Z"
    },
    {
      "booking_id": "bk_xyz789ghi012",
      "property_id": "prop_staymii_hotel",
      "room_id": "room_1702",
      "room_name": "Unit 1702 - Studio",
      "guest_name": "Sarah Lee",
      "check_in": "2026-03-20",
      "check_out": "2026-03-22",
      "nights": 2,
      "total_amount": 400.00,
      "currency": "MYR",
      "status": "confirmed",
      "source": "agoda",
      "invoice_status": "pending",
      "lhdn_uuid": null,
      "created_at": "2026-03-12T14:15:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 50,
    "total_results": 127,
    "total_pages": 3
  }
}`}</CodeBlock>
              </DocSection>

              {/* ═══ RESERVATION UPDATES ═══ */}
              <DocSection id="reservation-updates" icon={CalendarDays} title="Reservation Updates">
                <p className="mb-6">
                  Update existing reservations when changes occur in your PMS — guest modifications, date changes, cancellations, or amount adjustments.
                </p>

                {/* PUT /api/bookings/{id} */}
                <Endpoint 
                  method="PUT" 
                  path="/v1/bookings/{booking_id}" 
                  description="Update an existing booking. Only include fields that have changed. StayMii will automatically update linked invoices if applicable."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Path Parameters</h4>
                <ParamTable params={[
                  { name: 'booking_id', type: 'string', required: true, desc: 'The StayMii booking ID (e.g., bk_abc123def456)' },
                ]} />

                <h4 className="text-sm font-semibold text-slate-800 mb-2 mt-4">Request Body (partial update)</h4>
                <ParamTable params={[
                  { name: 'guest_name', type: 'string', required: false, desc: 'Updated guest name' },
                  { name: 'guest_email', type: 'string', required: false, desc: 'Updated guest email' },
                  { name: 'check_in', type: 'string (ISO 8601)', required: false, desc: 'Updated check-in date' },
                  { name: 'check_out', type: 'string (ISO 8601)', required: false, desc: 'Updated check-out date' },
                  { name: 'room_id', type: 'string', required: false, desc: 'Updated room assignment' },
                  { name: 'total_amount', type: 'number', required: false, desc: 'Updated total amount' },
                  { name: 'status', type: 'string', required: false, desc: 'Updated status: confirmed, cancelled, no_show, checked_in, checked_out' },
                  { name: 'notes', type: 'string', required: false, desc: 'Updated notes' },
                ]} />

                <CodeBlock title="Request — PUT /v1/bookings/bk_abc123def456" lang="json">{`{
  "check_out": "2026-03-20",
  "total_amount": 1250.00,
  "notes": "Extended stay - 2 additional nights"
}`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": {
    "booking_id": "bk_abc123def456",
    "property_id": "prop_staymii_hotel",
    "room_id": "room_1505",
    "room_name": "Unit 1505 - 2BR 2BA",
    "guest_name": "Ahmad bin Abdullah",
    "check_in": "2026-03-15",
    "check_out": "2026-03-20",
    "nights": 5,
    "total_amount": 1250.00,
    "currency": "MYR",
    "status": "confirmed",
    "invoice_status": "pending_update",
    "updated_at": "2026-03-16T10:00:00Z",
    "changes": [
      "check_out: 2026-03-18 → 2026-03-20",
      "total_amount: 750.00 → 1250.00",
      "nights: 3 → 5"
    ]
  }
}`}</CodeBlock>

                {/* DELETE /api/bookings/{id} */}
                <Endpoint 
                  method="DELETE" 
                  path="/v1/bookings/{booking_id}" 
                  description="Cancel a booking. If an e-invoice has been submitted, a cancellation request will be sent to LHDN."
                />

                <CodeBlock title="Request — DELETE /v1/bookings/bk_abc123def456" lang="bash">{`curl -X DELETE "https://api.staymii.com/v1/bookings/bk_abc123def456" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": {
    "booking_id": "bk_abc123def456",
    "status": "cancelled",
    "invoice_status": "cancellation_submitted",
    "lhdn_cancellation_id": "CAN-2026-00089",
    "cancelled_at": "2026-03-16T11:30:00Z"
  }
}`}</CodeBlock>
              </DocSection>

              {/* ═══ PROPERTIES ═══ */}
              <DocSection id="properties" icon={BedDouble} title="Properties">
                <p className="mb-6">
                  Retrieve property information to map your PMS properties to StayMii. This endpoint is essential 
                  for multi-property integrations and initial onboarding.
                </p>

                <Endpoint 
                  method="GET" 
                  path="/v1/properties" 
                  description="List all properties accessible to your partner account."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Query Parameters</h4>
                <ParamTable params={[
                  { name: 'country', type: 'string', required: false, desc: 'Filter by country code (e.g., MY)' },
                  { name: 'page', type: 'integer', required: false, desc: 'Page number (default: 1)' },
                  { name: 'per_page', type: 'integer', required: false, desc: 'Results per page (default: 50)' },
                ]} />

                <CodeBlock title="Request — GET /v1/properties" lang="bash">{`curl -X GET "https://api.staymii.com/v1/properties" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": [
    {
      "property_id": "prop_staymii_hotel",
      "name": "StayMii Hotel",
      "address": "Jalan Sultan Ahmad Shah, Georgetown, Penang",
      "timezone": "Asia/Kuala_Lumpur",
      "currency": "MYR",
      "country": "MY",
      "total_rooms": 60,
      "star_rating": 4,
      "contact_email": "ops@staymii.com",
      "contact_phone": "+604-2171000"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 50,
    "total_results": 1,
    "total_pages": 1
  }
}`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Response Fields</h3>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: true, desc: 'Unique StayMii property identifier' },
                  { name: 'name', type: 'string', required: true, desc: 'Property display name' },
                  { name: 'address', type: 'string', required: true, desc: 'Full property address' },
                  { name: 'timezone', type: 'string', required: true, desc: 'IANA timezone (e.g., Asia/Kuala_Lumpur)' },
                  { name: 'currency', type: 'string', required: true, desc: 'Default currency code (ISO 4217)' },
                  { name: 'country', type: 'string', required: true, desc: 'Country code (ISO 3166-1 alpha-2)' },
                ]} />
              </DocSection>

              {/* ═══ GUESTS ═══ */}
              <DocSection id="guests" icon={Users} title="Guests">
                <p className="mb-6">
                  Access guest profiles independently from bookings. These endpoints support CRM integrations, 
                  loyalty programs, and guest history lookups.
                </p>

                <Endpoint 
                  method="GET" 
                  path="/v1/guests" 
                  description="Retrieve a list of guest profiles with optional search and filtering."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Query Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: false, desc: 'Filter by property' },
                  { name: 'search', type: 'string', required: false, desc: 'Search by guest name, email, or phone' },
                  { name: 'country', type: 'string', required: false, desc: 'Filter by guest nationality (ISO 3166-1)' },
                  { name: 'page', type: 'integer', required: false, desc: 'Page number (default: 1)' },
                  { name: 'per_page', type: 'integer', required: false, desc: 'Results per page (default: 50, max: 200)' },
                ]} />

                <CodeBlock title="Request — GET /v1/guests" lang="bash">{`curl -X GET "https://api.staymii.com/v1/guests?search=Ahmad&property_id=prop_staymii_hotel" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": [
    {
      "guest_id": "gst_a1b2c3d4",
      "name": "Ahmad bin Abdullah",
      "email": "ahmad@example.com",
      "phone": "+60123456789",
      "country": "MY",
      "id_type": "NRIC",
      "total_stays": 5,
      "total_nights": 14,
      "total_spent": 4250.00,
      "currency": "MYR",
      "last_stay": "2026-03-18",
      "first_stay": "2025-08-10",
      "created_at": "2025-08-10T12:00:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 50,
    "total_results": 1,
    "total_pages": 1
  }
}`}</CodeBlock>

                <Endpoint 
                  method="GET" 
                  path="/v1/guests/{guest_id}" 
                  description="Retrieve a single guest profile with full booking history."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Path Parameters</h4>
                <ParamTable params={[
                  { name: 'guest_id', type: 'string', required: true, desc: 'The StayMii guest ID (e.g., gst_a1b2c3d4)' },
                ]} />

                <CodeBlock title="Request — GET /v1/guests/gst_a1b2c3d4" lang="bash">{`curl -X GET "https://api.staymii.com/v1/guests/gst_a1b2c3d4" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": {
    "guest_id": "gst_a1b2c3d4",
    "name": "Ahmad bin Abdullah",
    "email": "ahmad@example.com",
    "phone": "+60123456789",
    "country": "MY",
    "id_type": "NRIC",
    "id_number": "901215-14-****",
    "total_stays": 5,
    "total_nights": 14,
    "total_spent": 4250.00,
    "currency": "MYR",
    "booking_history": [
      {
        "booking_id": "bk_abc123def456",
        "property_id": "prop_staymii_hotel",
        "room_name": "Unit 1505 - 2BR 2BA",
        "check_in": "2026-03-15",
        "check_out": "2026-03-18",
        "total_amount": 750.00,
        "status": "confirmed",
        "source": "booking_com"
      },
      {
        "booking_id": "bk_older_stay_001",
        "property_id": "prop_staymii_hotel",
        "room_name": "Unit 1702 - Studio",
        "check_in": "2025-12-20",
        "check_out": "2025-12-23",
        "total_amount": 600.00,
        "status": "checked_out",
        "source": "direct"
      }
    ],
    "preferences": {
      "room_type": "2br_2ba",
      "floor_preference": "high",
      "special_requests": ["Late check-in", "Extra pillows"]
    }
  }
}`}</CodeBlock>
              </DocSection>

              {/* ═══ AVAILABILITY & INVENTORY ═══ */}
              <DocSection id="availability-inventory" icon={BedDouble} title="Availability & Inventory">
                <p className="mb-6">
                  Query rooms, units, and real-time availability across your properties. 
                  Use these endpoints to display available inventory or validate bookings before creation.
                </p>

                {/* GET /api/rooms */}
                <Endpoint 
                  method="GET" 
                  path="/v1/rooms" 
                  description="Retrieve all rooms and units for a property, including room type, capacity, and current status."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Query Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: true, desc: 'Property to query rooms for' },
                  { name: 'room_type', type: 'string', required: false, desc: 'Filter by room type: studio, 1br, 2br_1ba, 2br_2ba, 3br' },
                  { name: 'status', type: 'string', required: false, desc: 'Filter by status: active, inactive, maintenance' },
                ]} />

                <CodeBlock title="Request — GET /v1/rooms" lang="bash">{`curl -X GET "https://api.staymii.com/v1/rooms?property_id=prop_staymii_hotel" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": [
    {
      "room_id": "room_1505",
      "property_id": "prop_staymii_hotel",
      "room_name": "Unit 1505",
      "room_type": "2br_2ba",
      "room_type_label": "2 Bedroom 2 Bathroom",
      "floor": 15,
      "max_occupancy": 6,
      "base_rate": 250.00,
      "currency": "MYR",
      "amenities": ["wifi", "kitchen", "washer", "parking", "pool"],
      "status": "active",
      "pms_room_id": "beds24_room_98765"
    },
    {
      "room_id": "room_1702",
      "property_id": "prop_staymii_hotel",
      "room_name": "Unit 1702",
      "room_type": "studio",
      "room_type_label": "Studio",
      "floor": 17,
      "max_occupancy": 2,
      "base_rate": 180.00,
      "currency": "MYR",
      "amenities": ["wifi", "kitchen", "parking", "pool"],
      "status": "active",
      "pms_room_id": "beds24_room_98770"
    }
  ],
  "total": 45
}`}</CodeBlock>

                {/* GET /api/availability */}
                <Endpoint 
                  method="GET" 
                  path="/v1/availability" 
                  description="Check room availability for a specific date range. Returns available rooms with pricing."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Query Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: true, desc: 'Property to check availability for' },
                  { name: 'check_in', type: 'string (ISO 8601)', required: true, desc: 'Desired check-in date' },
                  { name: 'check_out', type: 'string (ISO 8601)', required: true, desc: 'Desired check-out date' },
                  { name: 'room_type', type: 'string', required: false, desc: 'Filter by room type' },
                  { name: 'guests', type: 'integer', required: false, desc: 'Number of guests (filters by max_occupancy)' },
                ]} />

                <CodeBlock title="Request — GET /v1/availability" lang="bash">{`curl -X GET "https://api.staymii.com/v1/availability?property_id=prop_staymii_hotel&check_in=2026-04-01&check_out=2026-04-05&room_type=2br_2ba" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": {
    "property_id": "prop_staymii_hotel",
    "property_name": "StayMii Hotel",
    "check_in": "2026-04-01",
    "check_out": "2026-04-05",
    "nights": 4,
    "available_rooms": [
      {
        "room_id": "room_1505",
        "room_name": "Unit 1505",
        "room_type": "2br_2ba",
        "floor": 15,
        "max_occupancy": 6,
        "rate_per_night": 250.00,
        "total_amount": 1000.00,
        "currency": "MYR",
        "rate_plan": "standard"
      },
      {
        "room_id": "room_2202",
        "room_name": "Unit 2202",
        "room_type": "2br_2ba",
        "floor": 22,
        "max_occupancy": 6,
        "rate_per_night": 280.00,
        "total_amount": 1120.00,
        "currency": "MYR",
        "rate_plan": "premium"
      }
    ],
    "unavailable_count": 12
  }
}`}</CodeBlock>
              </DocSection>

              {/* ═══ RATES ═══ */}
              <DocSection id="rates" icon={BarChart3} title="Rates">
                <p className="mb-6">
                  Manage room rates and pricing. Push rate updates from your PMS or channel manager to keep pricing synchronized across all platforms.
                </p>

                {/* GET /api/rates */}
                <Endpoint 
                  method="GET" 
                  path="/v1/rates" 
                  description="Retrieve current rates for rooms within a date range."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Query Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: true, desc: 'Property to retrieve rates for' },
                  { name: 'room_id', type: 'string', required: false, desc: 'Specific room (omit for all rooms)' },
                  { name: 'date_from', type: 'string (ISO 8601)', required: true, desc: 'Start date for rate range' },
                  { name: 'date_to', type: 'string (ISO 8601)', required: true, desc: 'End date for rate range' },
                ]} />

                <CodeBlock title="Request — GET /v1/rates" lang="bash">{`curl -X GET "https://api.staymii.com/v1/rates?property_id=prop_staymii_hotel&room_id=room_1505&date_from=2026-04-01&date_to=2026-04-07" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "X-Partner-ID: partner_12345"`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": {
    "property_id": "prop_staymii_hotel",
    "room_id": "room_1505",
    "room_name": "Unit 1505 - 2BR 2BA",
    "currency": "MYR",
    "rates": [
      { "date": "2026-04-01", "rate": 250.00, "min_stay": 1, "available": true },
      { "date": "2026-04-02", "rate": 250.00, "min_stay": 1, "available": true },
      { "date": "2026-04-03", "rate": 280.00, "min_stay": 1, "available": true },
      { "date": "2026-04-04", "rate": 320.00, "min_stay": 2, "available": true },
      { "date": "2026-04-05", "rate": 320.00, "min_stay": 2, "available": false },
      { "date": "2026-04-06", "rate": 250.00, "min_stay": 1, "available": true },
      { "date": "2026-04-07", "rate": 250.00, "min_stay": 1, "available": true }
    ]
  }
}`}</CodeBlock>

                {/* POST /api/rates */}
                <Endpoint 
                  method="POST" 
                  path="/v1/rates" 
                  description="Push rate updates for one or more rooms across a date range. Supports bulk updates for efficient sync."
                />
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Request Body Parameters</h4>
                <ParamTable params={[
                  { name: 'property_id', type: 'string', required: true, desc: 'Property to update rates for' },
                  { name: 'updates', type: 'array', required: true, desc: 'Array of rate update objects' },
                  { name: 'updates[].room_id', type: 'string', required: true, desc: 'Room to update' },
                  { name: 'updates[].date_from', type: 'string (ISO 8601)', required: true, desc: 'Rate period start' },
                  { name: 'updates[].date_to', type: 'string (ISO 8601)', required: true, desc: 'Rate period end' },
                  { name: 'updates[].rate', type: 'number', required: true, desc: 'Nightly rate in property currency' },
                  { name: 'updates[].min_stay', type: 'integer', required: false, desc: 'Minimum night stay requirement' },
                  { name: 'updates[].closed', type: 'boolean', required: false, desc: 'Close dates for booking (stop-sell)' },
                ]} />

                <CodeBlock title="Request — POST /v1/rates" lang="json">{`{
  "property_id": "prop_staymii_hotel",
  "updates": [
    {
      "room_id": "room_1505",
      "date_from": "2026-04-10",
      "date_to": "2026-04-15",
      "rate": 350.00,
      "min_stay": 2
    },
    {
      "room_id": "room_1702",
      "date_from": "2026-04-10",
      "date_to": "2026-04-15",
      "rate": 220.00,
      "min_stay": 1,
      "closed": false
    }
  ]
}`}</CodeBlock>

                <CodeBlock title="Response — 200 OK" lang="json">{`{
  "success": true,
  "data": {
    "updated_count": 2,
    "updates": [
      {
        "room_id": "room_1505",
        "dates_affected": 6,
        "rate": 350.00,
        "status": "applied"
      },
      {
        "room_id": "room_1702",
        "dates_affected": 6,
        "rate": 220.00,
        "status": "applied"
      }
    ],
    "sync_timestamp": "2026-03-18T09:00:00Z"
  }
}`}</CodeBlock>
              </DocSection>

              {/* ═══ WEBHOOKS ═══ */}
              <DocSection id="webhooks" icon={Bell} title="Webhooks">
                <p className="mb-6">
                  Receive real-time notifications when events occur in StayMii. Configure webhook URLs in your partner dashboard 
                  to get instant updates on booking changes, invoice status, and LHDN submissions.
                </p>

                <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3">Supported Events</h3>
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm" data-testid="webhook-events-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 pr-4 text-slate-500 font-medium text-xs uppercase tracking-wider">Event</th>
                        <th className="text-left py-2 text-slate-500 font-medium text-xs uppercase tracking-wider">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['booking.created', 'A new booking has been synced or created'],
                        ['booking.updated', 'Booking details were modified (dates, amount, room)'],
                        ['booking.cancelled', 'A booking was cancelled'],
                        ['booking.checked_in', 'Guest has checked in'],
                        ['booking.checked_out', 'Guest has checked out'],
                        ['invoice.created', 'A draft e-invoice was generated for a booking'],
                        ['invoice.submitted', 'Invoice was submitted to LHDN MyInvois'],
                        ['invoice.validated', 'LHDN accepted and validated the invoice'],
                        ['invoice.rejected', 'LHDN rejected the invoice (see rejection reason)'],
                        ['invoice.cancelled', 'Invoice cancellation was processed'],
                      ].map(([event, desc]) => (
                        <tr key={event} className="border-b border-slate-100">
                          <td className="py-2.5 pr-4"><code className="text-xs font-mono text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{event}</code></td>
                          <td className="py-2.5 text-slate-600 text-xs">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Webhook Configuration</h3>
                <CodeBlock title="Register Webhook — POST /v1/webhooks" lang="json">{`{
  "url": "https://your-pms.com/webhooks/staymii",
  "events": [
    "booking.created",
    "booking.updated",
    "booking.cancelled",
    "invoice.validated",
    "invoice.rejected"
  ],
  "secret": "whsec_your_signing_secret"
}`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Webhook Payload Example</h3>
                <p className="text-sm mb-2">Each webhook delivery includes a signature header for verification:</p>
                <CodeBlock title="Webhook Headers" lang="http">{`POST /webhooks/staymii HTTP/1.1
Content-Type: application/json
X-StayMii-Signature: sha256=abc123...
X-StayMii-Event: invoice.validated
X-StayMii-Delivery: evt_delivery_001`}</CodeBlock>

                <CodeBlock title="Webhook Payload — invoice.validated" lang="json">{`{
  "event": "invoice.validated",
  "timestamp": "2026-03-18T14:30:00Z",
  "data": {
    "booking_id": "bk_abc123def456",
    "invoice_id": "inv_98765",
    "property_id": "prop_staymii_hotel",
    "guest_name": "Ahmad bin Abdullah",
    "total_amount": 750.00,
    "currency": "MYR",
    "lhdn_uuid": "INV-2026-00456",
    "lhdn_long_id": "ABCDEFGH12345678",
    "validation_date": "2026-03-18T14:29:55Z",
    "qr_code_url": "https://myinvois.hasil.gov.my/ABCDEFGH12345678",
    "pdf_url": "https://api.staymii.com/v1/invoices/inv_98765/pdf"
  }
}`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Signature Verification</h3>
                <p className="text-sm mb-2">Verify webhook authenticity by computing the HMAC-SHA256 signature:</p>
                <CodeBlock title="Verification Example (Python)" lang="python">{`import hmac
import hashlib

def verify_webhook(payload_body, signature_header, secret):
    expected = hmac.new(
        secret.encode('utf-8'),
        payload_body,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature_header)`}</CodeBlock>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-4">
                  <p className="text-sm text-amber-800">
                    <strong>Retry Policy:</strong> StayMii retries failed webhook deliveries up to 5 times with exponential backoff 
                    (1 min, 5 min, 30 min, 2 hr, 12 hr). Your endpoint must return a 2xx status within 10 seconds.
                  </p>
                </div>
              </DocSection>

              {/* ═══ ERROR CODES ═══ */}
              <DocSection id="error-codes" icon={AlertTriangle} title="Error Codes">
                <p className="mb-6">
                  StayMii uses standard HTTP status codes. All error responses follow a consistent format with machine-readable codes and human-readable messages.
                </p>

                <h3 className="text-lg font-semibold text-slate-900 mb-3">Error Response Format</h3>
                <CodeBlock title="Standard Error Response" lang="json">{`{
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "The check_out date must be after the check_in date.",
    "status": 422,
    "details": {
      "field": "check_out",
      "value": "2026-03-10",
      "constraint": "must be after check_in (2026-03-15)"
    }
  }
}`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">HTTP Status Codes</h3>
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm" data-testid="error-codes-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 pr-4 text-slate-500 font-medium text-xs uppercase tracking-wider">Status</th>
                        <th className="text-left py-2 pr-4 text-slate-500 font-medium text-xs uppercase tracking-wider">Code</th>
                        <th className="text-left py-2 text-slate-500 font-medium text-xs uppercase tracking-wider">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['200', 'OK', 'Request succeeded'],
                        ['201', 'CREATED', 'Resource created successfully'],
                        ['400', 'BAD_REQUEST', 'Malformed request body or missing required fields'],
                        ['401', 'UNAUTHORIZED', 'Invalid or missing API key'],
                        ['403', 'FORBIDDEN', 'API key does not have access to this resource'],
                        ['404', 'NOT_FOUND', 'The requested resource does not exist'],
                        ['409', 'CONFLICT', 'Resource already exists (duplicate external_booking_id)'],
                        ['422', 'VALIDATION_ERROR', 'Request body fails validation (e.g., invalid dates)'],
                        ['429', 'RATE_LIMITED', 'Too many requests. Retry after the time in Retry-After header'],
                        ['500', 'INTERNAL_ERROR', 'Unexpected server error. Contact support if persistent'],
                        ['502', 'UPSTREAM_ERROR', 'Error communicating with LHDN MyInvois system'],
                        ['503', 'SERVICE_UNAVAILABLE', 'Scheduled maintenance or temporary outage'],
                      ].map(([status, code, desc]) => (
                        <tr key={status} className="border-b border-slate-100">
                          <td className="py-2.5 pr-4">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${
                              status.startsWith('2') ? 'bg-emerald-100 text-emerald-700' :
                              status.startsWith('4') ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{status}</span>
                          </td>
                          <td className="py-2.5 pr-4"><code className="text-xs font-mono text-slate-700">{code}</code></td>
                          <td className="py-2.5 text-slate-600 text-xs">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Rate Limiting</h3>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <p className="text-sm text-slate-700 mb-3">
                    API requests are rate-limited per partner. The following headers are included in every response:
                  </p>
                  <CodeBlock title="Rate Limit Headers" lang="http">{`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 847
X-RateLimit-Reset: 1711800000
Retry-After: 60`}</CodeBlock>
                  <ul className="text-sm text-slate-600 space-y-1 mt-3">
                    <li><strong>Standard tier:</strong> 1,000 requests per hour</li>
                    <li><strong>Premium tier:</strong> 10,000 requests per hour</li>
                    <li><strong>Bulk sync operations:</strong> 100 requests per minute</li>
                  </ul>
                </div>
              </DocSection>

              {/* ═══ SANDBOX / TEST MODE ═══ */}
              <DocSection id="sandbox" icon={FlaskConical} title="Sandbox / Test Mode">
                <p className="mb-4">
                  Use the sandbox environment to develop and test your integration without affecting production data. 
                  The sandbox mirrors the production API with test data and simulated LHDN responses.
                </p>

                <div className="flex items-stretch rounded-xl overflow-hidden border border-slate-200 my-6">
                  <div className="bg-amber-500 text-white px-4 py-3 flex items-center shrink-0">
                    <span className="text-xs font-bold uppercase tracking-wider">Sandbox</span>
                  </div>
                  <div className="flex-1 bg-slate-50 px-4 py-3 flex items-center">
                    <code className="text-sm font-mono text-slate-800">https://sandbox-api.staymii.com</code>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3">Test API Key</h3>
                <p className="text-sm mb-2">Use the following test key for sandbox requests:</p>
                <CodeBlock title="Sandbox Credentials" lang="text">{`API Key:        sk_test_xxxxxx
Partner ID:     partner_test_001
Property ID:    prop_test_mansion`}</CodeBlock>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Sandbox Features</h3>
                <div className="grid sm:grid-cols-2 gap-3 my-4">
                  {[
                    { title: 'Full API Parity', desc: 'All production endpoints are available in sandbox.' },
                    { title: 'Simulated LHDN', desc: 'e-Invoice submissions return mock validation responses.' },
                    { title: 'Test Bookings', desc: 'Pre-loaded sample bookings for immediate testing.' },
                    { title: 'Webhook Testing', desc: 'Trigger test webhook events on demand.' },
                    { title: 'No Rate Limits', desc: 'Relaxed rate limits for development and testing.' },
                    { title: 'Data Reset', desc: 'Reset sandbox data anytime via API or dashboard.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3">Quick Test</h3>
                <p className="text-sm mb-2">Verify your sandbox connection with a simple request:</p>
                <CodeBlock title="Test Connection" lang="bash">{`curl -X GET "https://sandbox-api.staymii.com/v1/properties" \\
  -H "Authorization: Bearer sk_test_xxxxxx" \\
  -H "X-Partner-ID: partner_test_001"`}</CodeBlock>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mt-6">
                  <p className="text-sm text-blue-800">
                    <strong>Going Live:</strong> When your integration passes sandbox testing, contact{' '}
                    <a href="mailto:developer@staymii.com" className="font-semibold underline">developer@staymii.com</a>{' '}
                    to receive your production API key. Simply swap the base URL and key &mdash; no code changes required.
                  </p>
                </div>
              </DocSection>

              {/* ═══ API CHANGELOG ═══ */}
              <DocSection id="changelog" icon={History} title="API Changelog">
                <p className="mb-6">
                  Track API updates, new endpoints, and breaking changes. We follow semantic versioning 
                  and announce deprecations at least 90 days in advance.
                </p>

                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-700 text-xs font-semibold">
                    Current Version: v1
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-700 text-xs font-semibold">
                    Status: Stable
                  </span>
                </div>

                <div className="space-y-6">
                  {/* Entry: 2026-03-18 */}
                  <div className="relative pl-6 border-l-2 border-emerald-500">
                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" />
                    <div className="mb-1 flex items-center gap-3">
                      <span className="text-sm font-bold text-slate-900">2026-03-18</span>
                      <span className="inline-flex px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold uppercase tracking-wider">Initial Release</span>
                    </div>
                    <p className="text-sm text-slate-700 font-medium mb-2">API v1 &mdash; Initial Public Release</p>
                    <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
                      <li>Booking Sync endpoints &mdash; create, retrieve, update, and cancel bookings</li>
                      <li>Properties endpoint &mdash; list and query property details</li>
                      <li>Guests endpoint &mdash; guest profiles with booking history</li>
                      <li>Availability &amp; Inventory &mdash; real-time room availability queries</li>
                      <li>Rate Management &mdash; push and pull nightly rates</li>
                      <li>Webhooks &mdash; real-time event notifications with HMAC signature verification</li>
                      <li>LHDN MyInvois e-invoice automation integration</li>
                      <li>Idempotency protection for all write operations</li>
                      <li>Sandbox environment for development and testing</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-8">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Deprecation Policy</h4>
                  <ul className="text-xs text-slate-600 space-y-1.5 list-disc list-inside">
                    <li>Breaking changes are announced at least <strong>90 days</strong> before enforcement</li>
                    <li>Deprecated endpoints return a <code className="bg-slate-200 px-1 py-0.5 rounded text-[10px]">X-StayMii-Deprecated: true</code> header</li>
                    <li>Subscribe to <a href="mailto:developer@staymii.com" className="text-[#2563EB] underline">developer@staymii.com</a> for changelog notifications</li>
                  </ul>
                </div>
              </DocSection>

              {/* ═══ CTA ═══ */}
              <div className="mt-16 bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-8 lg:p-12 text-center" data-testid="api-docs-cta">
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">Ready to Integrate?</h2>
                <p className="text-white/70 mb-8 max-w-xl mx-auto">
                  Contact our integration team to get your API credentials and start building your StayMii integration.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold transition-all hover:shadow-lg"
                    data-testid="cta-request-access"
                  >
                    Request API Access <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a 
                    href="mailto:developer@staymii.com" 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-xl font-semibold transition-all hover:bg-white/20 border border-white/20"
                    data-testid="cta-contact-dev"
                  >
                    <Mail className="w-4 h-4" /> developer@staymii.com
                  </a>
                </div>
              </div>

            </main>
          </div>
        </div>

        <LandingFooter />
      </div>
    </>
  );
};

export default ApiDocs;
